import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { StyledCircularProgress } from "../../../components/Loading/styles";
import { PdfViewer } from "../../../components/PDFViewer/PDFViewer";
import useMailingOnBoardingService from "../../../shared/services/useMailingOnBoardingService";
import { ContractLoading } from "../components/Contract/styles";
import { EMailingStepsEnum, MailingStepProps } from "../index.types";
import { BoldTypography, SubTitleInterTypography } from "../styles";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ContractStep = ({ setActiveStep }: MailingStepProps) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [usePdfViewer, setUsePdfViewer] = useState<boolean>(false);
  const [isSecondDocument, setIsSecondDocument] = useState(false);
  const { getContract } = useMailingOnBoardingService();

  // Usar useCallback para estabilizar a função e evitar re-renders
  const fetchContract = useCallback(async () => {
    if (!id || isLoading || pdfUrl) return;
    
    setIsLoading(true);
    
    console.log('ContractStep - Carregando contrato para LEITURA (sem assinatura)...');
    
    // PRIORIDADE 1: Segundo contrato não assinado (para leitura)
    const secondContract = localStorage.getItem('secondContractDocument');
    if (secondContract) {
      try {
        // Processar segundo contrato não assinado
        let cleanBase64 = secondContract;
        
        // Remover prefixo data URL se existir
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        
        // Remover quebras de linha e espaços
        cleanBase64 = cleanBase64.replace(/[\r\n\s]/g, '');
        
        console.log('ContractStep - Contrato assinado, tamanho:', cleanBase64.length);
        
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        // Validar se é um PDF válido
        const headerBytes = byteArray.slice(0, 4);
        let pdfHeader = '';
        for (let i = 0; i < headerBytes.length; i++) {
          pdfHeader += String.fromCharCode(headerBytes[i]);
        }
        if (pdfHeader !== '%PDF') {
          throw new Error('Contrato assinado não é um PDF válido');
        }
        
        // Usar data URL diretamente do base64 limpo
        const dataUrl = `data:application/pdf;base64,${cleanBase64}`;
        
        console.log('ContractStep - PDF Data URL criada do contrato assinado');
        
        // Validar se o data URL foi criado corretamente
        if (!dataUrl.startsWith('data:application/pdf;base64,')) {
          throw new Error('Erro ao criar data URL do contrato assinado');
        }
        
        console.log('ContractStep - Mostrando segundo contrato para LEITURA');
        setPdfUrl(dataUrl);
        setUsePdfViewer(true);
        setIsSecondDocument(true); // Marcar que é o segundo documento
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('ContractStep - Erro ao processar contrato assinado:', error);
      }
    }

    // PRIORIDADE 2: Primeiro contrato não assinado (para leitura)
    const createdContract = localStorage.getItem('contractDocument');
    console.log('ContractStep - Primeiro contrato não assinado:', createdContract ? 'Encontrado' : 'Não encontrado');
    
    if (createdContract) {
      try {
        // Limpar o base64 removendo prefixos e caracteres inválidos
        let cleanBase64 = createdContract;
        
        // Remover prefixo data URL se existir
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        
        // Remover quebras de linha e espaços
        cleanBase64 = cleanBase64.replace(/[\r\n\s]/g, '');
        
        console.log('ContractStep - Base64 limpo, tamanho:', cleanBase64.length);
        
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        // Validar se é um PDF válido
        const headerBytes = byteArray.slice(0, 4);
        let pdfHeader = '';
        for (let i = 0; i < headerBytes.length; i++) {
          pdfHeader += String.fromCharCode(headerBytes[i]);
        }
        if (pdfHeader !== '%PDF') {
          throw new Error('Dados do localStorage não são um PDF válido');
        }
        
        // Usar data URL diretamente do base64 limpo
        const dataUrl = `data:application/pdf;base64,${cleanBase64}`;
        
        console.log('ContractStep - Mostrando primeiro contrato para LEITURA');
        console.log('ContractStep - Tamanho do base64:', cleanBase64.length);
        
        // Validar se o data URL foi criado corretamente
        if (!dataUrl.startsWith('data:application/pdf;base64,')) {
          throw new Error('Erro ao criar data URL do PDF do localStorage');
        }
        
        setPdfUrl(dataUrl);
        setUsePdfViewer(true);
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('ContractStep - Erro ao processar localStorage:', error);
      }
    }
    
    // PRIORIDADE 3: Fallback para backend (sempre buscar contrato NÃO assinado para leitura)
    try {
      console.log('ContractStep - Buscando contrato NÃO assinado do backend como fallback...');
      
      const contractData = {
        order: 1,
        unidadeId: 80,
        publicId: id,
        sign: "notAsign" // Sempre buscar contrato NÃO assinado para leitura
      };
      
      const contractResponse = await getContract(contractData);
      console.log('ContractStep - Contrato encontrado no backend');
      
      // Validar se a resposta é válida
      if (!contractResponse || contractResponse.byteLength === 0) {
        throw new Error('Resposta vazia do backend');
      }
      
      // Validar se é um PDF válido (verificar magic number)
      const uint8Array = new Uint8Array(contractResponse);
      const headerBytes = uint8Array.slice(0, 4);
      let pdfHeader = '';
      for (let i = 0; i < headerBytes.length; i++) {
        pdfHeader += String.fromCharCode(headerBytes[i]);
      }
      if (pdfHeader !== '%PDF') {
        throw new Error('Resposta não é um PDF válido');
      }
      
      // Converter ArrayBuffer para data URL (evita requisições HTTP)
      const bytes = new Uint8Array(contractResponse);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);
      const dataUrl = `data:application/pdf;base64,${base64}`;
      
      console.log('ContractStep - PDF Data URL criada do backend');
      console.log('ContractStep - Tamanho do base64:', base64.length);
      
      // Validar se o data URL foi criado corretamente
      if (!dataUrl.startsWith('data:application/pdf;base64,')) {
        throw new Error('Erro ao criar data URL do PDF');
      }
      
      setPdfUrl(dataUrl);
      setUsePdfViewer(true);
              setIsLoading(false);
      return;
      
    } catch (error) {
      console.log('ContractStep - Erro ao buscar do backend:', error);
      
      // Se não há documento em lugar nenhum, mostrar erro
      console.error('ContractStep - Nenhum documento de contrato foi encontrado.');
      setIsLoading(false);
    }
  }, [id, getContract]); // Dependências estáveis

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const handleUploadContract = async () => {
    console.log('ContractStep - handleUploadContract chamado, isSecondDocument:', isSecondDocument);
    setIsUploadingContract(true);

    await delay(2000);

    try {
      // O documento já foi criado pelo endpoint /v2/Contract/create
      // Agora só precisamos prosseguir para a assinatura
        setIsUploadingContract(false);
        setActiveStep(EMailingStepsEnum.ContractSignature);
    } catch (error) {
      setIsUploadingContract(false);
      console.error("Erro ao processar contrato:", error);
    }
  };

  useEffect(() => {
    const handleKeydownBlur = (event) => {
      if (event.key == "PrintScreen") {
        event.preventDefault();
        document.getElementById("mailing-contract").style.filter = "blur(5px)";
      }
    };
    const handleWindowBlur = () => {
      document.getElementById("mailing-contract").style.filter = "blur(5px)";
    };
    const handleWindowFocus = () => {
      document.getElementById("mailing-contract").style.filter = "none";
    };
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("keydown", handleKeydownBlur);
    
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("keydown", handleKeydownBlur);
      
      // Clean up blob URL if it exists to prevent memory leaks
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Stack>
      {isUploadingContract && (
        <ContractLoading>
          <StyledCircularProgress size={48} />
        </ContractLoading>
      )}
      <Box
        ref={containerRef}
        id="mailing-contract"
        sx={{
                width: "100%",
          maxWidth: "100%",
                margin: "0 auto",
          padding: { xs: "16px", sm: "24px" },
        }}
      >
        {usePdfViewer && pdfUrl ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: '900px',
              margin: '0 auto',
              backgroundColor: '#f5f5f5',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '& .react-pdf__Document': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              },
              '& .react-pdf__Page': {
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                borderRadius: '4px',
                overflow: 'hidden',
              },
              '& .react-pdf__Page__canvas': {
                maxWidth: '100% !important',
                height: 'auto !important',
              }
            }}
          >
            <PdfViewer 
              pdfUrl={pdfUrl}
            />
          </Box>
        ) : (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="200px"
            flexDirection="column"
            gap={2}
          >
            <Typography variant="h6" color="error">
              Contrato não disponível
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Por favor, volte e complete o processo de criação do contrato.
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setActiveStep(EMailingStepsEnum.PersonalDataForm)}
            >
              Voltar aos dados pessoais
            </Button>
          </Box>
        )}
      </Box>

      <SubTitleInterTypography textAlign="center" marginTop="32px">
        Após revisar os dados, clique no botão{" "}
        <BoldTypography>
          &quot;{isSecondDocument ? "Assinar formulário adicional" : "Assinar formulário"}&quot;.
        </BoldTypography>
      </SubTitleInterTypography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="center"
        gap="24px"
        mt="48px"
        mb="24px"
      >
        <Button
          color="primary"
          variant="outlined"
          size="large"
          disabled={isLoading || isUploadingContract}
          onClick={() => setActiveStep(EMailingStepsEnum.PersonalDataForm)}
        >
          Voltar
        </Button>
        <Button
          color="primary"
          variant="contained"
          size="large"
          disabled={isLoading || isUploadingContract}
          sx={{ paddingInline: "24px" }}
          startIcon={
            isUploadingContract ? (
              <StyledCircularProgress size={16} />
            ) : undefined
          }
          onClick={handleUploadContract}
        >
          {isSecondDocument ? "Assinar formulário adicional" : "Assinar formulário"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ContractStep;
